import { useEffect } from "react";

/**
 * Per-route SEO tags and structured data without a heavy dependency.
 *
 * Sets document.title, meta description, canonical, OG/Twitter tags, and
 * Schema.org JSON-LD on mount / when inputs change. This runs client-side,
 * so it helps JS-rendering crawlers (Google). Build-time prerendering
 * (generate-route-html.js) bakes these into static HTML so non-JS crawlers
 * (social link previews, Bing) see them too.
 *
 * Call it near the top of a marketing or public page component:
 *   useSeo({
 *     title: "Pricing",
 *     description: "…",
 *     canonicalPath: "/pricing",
 *     jsonLd: [ ...schemaObjects ],   // optional
 *   });
 *
 * Omit `title` on the homepage to use the full brand title. `image` is optional
 * and defaults to /og-image.png; pass a path only for an image that exists in
 * public/.
 *
 * JSON-LD: the page's block is tagged data-seo-jsonld="true". A prerendered
 * route shell (scripts/generate-route-html.js) uses the same tag, so on a direct
 * load this adopts the shell's block instead of adding a second one, and removes
 * it on unmount. Untagged blocks (the site-wide ones in public/index.html) are
 * never touched.
 */
export const SITE_URL = "https://www.optimiciti.com";
export const BRAND_TITLE = "Optimiciti — Vehicle Routing & Scheduling Optimization";
export const DEFAULT_DESCRIPTION =
  "Plan vehicle routes and schedules around the constraints you set — time windows, capacities, vehicle types, shift hours. Free to try, no account needed.";
export const DEFAULT_IMAGE = "https://www.optimiciti.com/og-image.png";

export function resolveImageUrl(image) {
  if (!image) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
}

function upsertMeta(attr, key, content) {
  if (content == null || typeof document === "undefined") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href || typeof document === "undefined") return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(serializedJsonLd) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector('script[data-seo-jsonld="true"]');
  if (!serializedJsonLd) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-jsonld", "true");
    document.head.appendChild(el);
  }
  el.textContent = serializedJsonLd;
}

export default function useSeo({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  twitterCard = "summary_large_image",
  jsonLd,
} = {}) {
  const fullTitle = title ? `${title} | Optimiciti` : BRAND_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const path =
    canonicalPath ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = SITE_URL + path;
  const resolvedImage = resolveImageUrl(image);
  const jsonLdSerialized = jsonLd
    ? typeof jsonLd === "string"
      ? jsonLd
      : JSON.stringify(jsonLd)
    : null;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertCanonical(canonical);

    // Keep OG/Twitter in sync (helps Google; prerender covers social crawlers).
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", resolvedImage);

    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", resolvedImage);

    if (jsonLdSerialized) {
      upsertJsonLd(jsonLdSerialized);
    }

    return () => {
      // Clean up route-specific JSON-LD so it does not persist across client-side navigation
      if (jsonLdSerialized) {
        upsertJsonLd(null);
      }
    };
  }, [
    fullTitle,
    desc,
    canonical,
    resolvedImage,
    type,
    twitterCard,
    jsonLdSerialized,
  ]);
}
